import * as React from "react";
import type { Column, Registrant, SavedView } from "../api.js";
import { type FilterMap, type SortSpec, getCellValue } from "../filter.js";
import { Icon } from "./Icon.js";
import { ColumnManager } from "./ColumnManager.js";
import { EventFilter, type EventInfo } from "./EventFilter.js";

function uniqueValues(rows: readonly Registrant[], col: Column): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const v = getCellValue(r, col);
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => set.add(String(x)));
    else set.add(String(v));
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

function valueCounts(rows: readonly Registrant[], col: Column): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of rows) {
    const v = getCellValue(r, col);
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      for (const x of v) {
        const k = String(x);
        out.set(k, (out.get(k) ?? 0) + 1);
      }
    } else {
      const k = String(v);
      out.set(k, (out.get(k) ?? 0) + 1);
    }
  }
  return out;
}

export function FilterSidebar({
  rows,
  columns,
  events,
  filters,
  onChange,
  onClearAll,
  savedViews,
  activeViewId,
  activeViewDirty,
  onPickView,
  onSaveView,
  onRenameView,
  onUpdateActiveView,
  onDeleteView,
  collapsed,
  onToggleCollapsed,
  visibleIds,
  defaultVisibleIds,
  onColumnsChange,
  columnOrder,
  onColumnOrderChange,
}: {
  rows: Registrant[];
  columns: Column[];
  events: EventInfo[];
  filters: FilterMap;
  onChange: (f: FilterMap) => void;
  onClearAll: () => void;
  savedViews: SavedView[];
  activeViewId: string | null;
  activeViewDirty: boolean;
  onPickView: (v: SavedView) => void;
  onSaveView: () => void;
  onRenameView: (id: string, name: string) => void;
  onUpdateActiveView: () => void;
  onDeleteView: (id: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  visibleIds: string[];
  defaultVisibleIds: string[];
  onColumnsChange: (ids: string[]) => void;
  columnOrder: string[];
  onColumnOrderChange: (ids: string[]) => void;
}) {
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState<Record<string, boolean>>({
    events: true,
    views: true,
    filters: true,
    columns: false,
    Event: true, Identity: true, Contact: false, Membership: true, Custom: false,
  });

  const eventFilter = filters.eventName;
  const selectedEvents = eventFilter?.kind === "set" ? eventFilter.values : [];

  function toggleGroup(k: string) { setOpen((s) => ({ ...s, [k]: !s[k] })); }

  function setFilter(id: string, v: FilterMap[string] | undefined) {
    const next = { ...filters };
    if (v === undefined) delete next[id];
    else next[id] = v;
    onChange(next);
  }

  const activeCount = React.useMemo(() => {
    let n = 0;
    for (const [id, f] of Object.entries(filters)) {
      if (!f) continue;
      // eventName lives in its own dedicated Events section above; don't
      // double-count it in the generic Filters badge.
      if (id === "eventName") continue;
      switch (f.kind) {
        case "text": if (f.q) n++; break;
        case "bool": n++; break;
        case "set": if (f.values.length) n++; break;
        case "range": if (f.min !== undefined || f.max !== undefined) n++; break;
        case "dateRange": if (f.from || f.to) n++; break;
      }
    }
    return n;
  }, [filters]);

  if (collapsed) {
    return (
      <aside className="sidebar collapsed">
        <button className="rail-btn" onClick={onToggleCollapsed} title="Expand filters">
          <Icon name="chevRight" size={16} />
        </button>
        <button className="rail-btn" title="Events"><Icon name="calendar" size={16} /></button>
        <button className="rail-btn" title="Saved views"><Icon name="bookmark" size={16} /></button>
        <button className="rail-btn" title="Filters"><Icon name="filter" size={16} /></button>
        <button className="rail-btn" title="Columns"><Icon name="columns" size={16} /></button>
        <style>{sidebarCss}</style>
      </aside>
    );
  }

  const groupOrder = ["Event", "Identity", "Contact", "Membership", "Custom"] as const;

  return (
    <aside className="sidebar">
      <div className="sb-head">
        <div className="sb-title">
          <Icon name="dashboard" size={14} />
          <span>View</span>
        </div>
        <div className="sb-actions">
          <button className="icon-btn" onClick={onToggleCollapsed} title="Collapse">
            <Icon name="chevLeft" size={14} />
          </button>
        </div>
      </div>

      <div className="sb-scroll">
        <FacetGroup
          title="Events"
          icon="calendar"
          open={open.events ?? true}
          onToggle={() => toggleGroup("events")}
          badge={selectedEvents.length > 0 ? selectedEvents.length : undefined}
        >
          <EventFilter
            events={events}
            rows={rows}
            selected={selectedEvents}
            onChange={(values) => {
              setFilter("eventName", values.length ? { kind: "set", values } : undefined);
            }}
          />
        </FacetGroup>

        <FacetGroup title="Saved views" icon="bookmark" open={open.views ?? true} onToggle={() => toggleGroup("views")}>
          <ul className="view-list">
            {savedViews.map((v) => {
              const isActive = activeViewId === v.id;
              const isRenaming = renamingId === v.id;
              return (
                <React.Fragment key={v.id}>
                  <li
                    className={"view-item" + (isActive ? " active" : "")}
                    onClick={isRenaming ? undefined : () => onPickView(v)}
                  >
                    <Icon name="tag" size={13} />
                    {isRenaming ? (
                      <RenameInput
                        initial={v.name}
                        onCancel={() => setRenamingId(null)}
                        onSubmit={(name) => {
                          const trimmed = name.trim();
                          if (trimmed && trimmed !== v.name) onRenameView(v.id, trimmed);
                          setRenamingId(null);
                        }}
                      />
                    ) : (
                      <span className="view-name">{v.name}</span>
                    )}
                    {!isRenaming && (
                      <>
                        <button
                          className="view-act"
                          onClick={(e) => { e.stopPropagation(); setRenamingId(v.id); }}
                          title="Rename view"
                          aria-label="Rename view"
                        >
                          <Icon name="edit" size={12} />
                        </button>
                        <button
                          className="view-act"
                          onClick={(e) => { e.stopPropagation(); onDeleteView(v.id); }}
                          title="Delete view"
                          aria-label="Delete view"
                        >
                          <Icon name="x" size={12} />
                        </button>
                      </>
                    )}
                  </li>
                  {isActive && activeViewDirty && !isRenaming && (
                    <li
                      className="view-update"
                      onClick={(e) => { e.stopPropagation(); onUpdateActiveView(); }}
                    >
                      <Icon name="refresh" size={12} />
                      <span>Save changes to "{v.name}"</span>
                    </li>
                  )}
                </React.Fragment>
              );
            })}
            <li className="view-item dim" onClick={onSaveView}>
              <Icon name="plus" size={13} />
              <span className="view-name">Save current as view…</span>
            </li>
          </ul>
        </FacetGroup>

        <FacetGroup
          title="Filters"
          icon="filter"
          open={open.filters ?? true}
          onToggle={() => toggleGroup("filters")}
          badge={activeCount > 0 ? activeCount : undefined}
          action={activeCount > 0 ? { label: "Clear", onClick: onClearAll } : undefined}
        >
          <div className="nested-group">
            {groupOrder.map((group) => {
              const groupCols = columns.filter((c) => c.group === group);
              if (!groupCols.length) return null;
              const filterableCols = groupCols.filter((c) => isFilterable(c));
              if (!filterableCols.length) return null;
              return (
                <FacetGroup
                  key={group}
                  title={group}
                  icon={iconForGroup(group)}
                  open={open[group] ?? false}
                  onToggle={() => toggleGroup(group)}
                >
                  {filterableCols.map((col) => (
                    <FilterField
                      key={col.id}
                      col={col}
                      rows={rows}
                      filter={filters[col.id]}
                      onChange={(v) => setFilter(col.id, v)}
                    />
                  ))}
                </FacetGroup>
              );
            })}
          </div>
        </FacetGroup>

        <FacetGroup
          title="Columns"
          icon="columns"
          open={open.columns ?? false}
          onToggle={() => toggleGroup("columns")}
          badge={`${visibleIds.length}/${columns.length}`}
        >
          <ColumnManager
            columns={columns}
            visibleIds={visibleIds}
            defaultVisibleIds={defaultVisibleIds}
            onChange={onColumnsChange}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
          />
        </FacetGroup>
      </div>

      <style>{sidebarCss}</style>
    </aside>
  );
}

function iconForGroup(g: string): string {
  switch (g) {
    case "Event": return "calendar";
    case "Identity": return "user";
    case "Contact": return "mail";
    case "Membership": return "users";
    case "Custom": return "tag";
    default: return "filter";
  }
}

function isFilterable(col: Column): boolean {
  if (col.id === "fullName") return false;
  if (col.id === "email") return false;
  if (col.id === "phone") return false;
  if (col.id === "ticketCode") return false;
  // eventName has its own dedicated top-level section in the sidebar.
  if (col.id === "eventName") return false;
  return true;
}

function FacetGroup({
  title,
  icon,
  open,
  onToggle,
  children,
  badge,
  action,
}: {
  title: string;
  icon: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  badge?: number | string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="facet-group">
      <button className={"facet-head" + (open ? "" : " closed")} onClick={onToggle}>
        <span className="left">
          <Icon name={icon} size={13} /> {title}
          {badge !== undefined && <span className="facet-badge">{badge}</span>}
        </span>
        <span className="right">
          {action && (
            <span
              className="link-btn facet-action"
              role="button"
              onClick={(e) => { e.stopPropagation(); action.onClick(); }}
            >
              {action.label}
            </span>
          )}
          <Icon name="chevDown" size={14} className="chev" />
        </span>
      </button>
      {open && <div className="facet-body">{children}</div>}
    </div>
  );
}

function FilterField({
  col,
  rows,
  filter,
  onChange,
}: {
  col: Column;
  rows: readonly Registrant[];
  filter: FilterMap[string] | undefined;
  onChange: (v: FilterMap[string] | undefined) => void;
}) {
  if (col.type === "bool") {
    const current = filter?.kind === "bool" ? filter.value : null;
    const truthy = rows.filter((r) => Boolean(getCellValue(r, col))).length;
    const falsy = rows.length - truthy;
    return (
      <div style={{ marginBottom: 8 }}>
        <div className="facet-sub">{col.label}</div>
        <FacetRow
          label="Yes"
          count={truthy}
          checked={current === true}
          onToggle={() => onChange(current === true ? undefined : { kind: "bool", value: true })}
        />
        <FacetRow
          label="No"
          count={falsy}
          checked={current === false}
          onToggle={() => onChange(current === false ? undefined : { kind: "bool", value: false })}
        />
      </div>
    );
  }

  const isCategorical =
    col.type === "select"
    || col.type === "tags"
    || (col.type === "text" && (col.id === "eventName" || col.id === "ticketType" || col.id === "gender" || col.id === "membershipLevel"));

  if (isCategorical) {
    const opts = col.choices && col.choices.length ? col.choices : uniqueValues(rows, col);
    if (!opts.length) return null;
    const counts = valueCounts(rows, col);
    const selected = filter?.kind === "set" ? filter.values : [];
    return (
      <div style={{ marginBottom: 8 }}>
        <div className="facet-sub">{col.label}</div>
        {opts.map((o) => (
          <FacetRow
            key={o}
            label={o}
            count={counts.get(o) ?? 0}
            checked={selected.includes(o)}
            onToggle={() => {
              const next = selected.includes(o)
                ? selected.filter((x) => x !== o)
                : [...selected, o];
              onChange(next.length === 0 ? undefined : { kind: "set", values: next });
            }}
          />
        ))}
      </div>
    );
  }

  if (col.type === "number" || col.type === "currency") {
    const cur = filter?.kind === "range"
      ? { min: filter.min, max: filter.max }
      : { min: undefined as number | undefined, max: undefined as number | undefined };
    return (
      <div style={{ marginBottom: 8 }}>
        <div className="facet-sub">{col.label}</div>
        <div className="range-row">
          <input
            className="input"
            placeholder="Min"
            type="number"
            value={cur.min ?? ""}
            onChange={(e) => {
              const min = e.target.value === "" ? undefined : Number(e.target.value);
              if (min === undefined && cur.max === undefined) { onChange(undefined); return; }
              const range: FilterMap[string] = { kind: "range" };
              if (min !== undefined) range.min = min;
              if (cur.max !== undefined) range.max = cur.max;
              onChange(range);
            }}
          />
          <input
            className="input"
            placeholder="Max"
            type="number"
            value={cur.max ?? ""}
            onChange={(e) => {
              const max = e.target.value === "" ? undefined : Number(e.target.value);
              if (cur.min === undefined && max === undefined) { onChange(undefined); return; }
              const range: FilterMap[string] = { kind: "range" };
              if (cur.min !== undefined) range.min = cur.min;
              if (max !== undefined) range.max = max;
              onChange(range);
            }}
          />
        </div>
      </div>
    );
  }

  if (col.type === "date" || col.type === "datetime") {
    const cur = filter?.kind === "dateRange"
      ? { from: filter.from, to: filter.to }
      : { from: undefined as string | undefined, to: undefined as string | undefined };
    return (
      <div style={{ marginBottom: 8 }}>
        <div className="facet-sub">{col.label}</div>
        <div className="range-row">
          <input
            className="input"
            type="date"
            value={cur.from ?? ""}
            onChange={(e) => {
              const from = e.target.value || undefined;
              if (!from && !cur.to) { onChange(undefined); return; }
              const range: FilterMap[string] = { kind: "dateRange" };
              if (from) range.from = from;
              if (cur.to) range.to = cur.to;
              onChange(range);
            }}
          />
          <input
            className="input"
            type="date"
            value={cur.to ?? ""}
            onChange={(e) => {
              const to = e.target.value || undefined;
              if (!cur.from && !to) { onChange(undefined); return; }
              const range: FilterMap[string] = { kind: "dateRange" };
              if (cur.from) range.from = cur.from;
              if (to) range.to = to;
              onChange(range);
            }}
          />
        </div>
      </div>
    );
  }

  return null;
}

function FacetRow({
  label,
  count,
  checked,
  onToggle,
}: {
  label: string;
  count: number;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="facet-row">
      <input type="checkbox" className="checkbox" checked={checked} onChange={onToggle} />
      <span className="lbl">{label}</span>
      <span className="num">{count}</span>
    </label>
  );
}

function RenameInput({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = React.useState(initial);
  return (
    <input
      className="rename-input"
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onBlur={() => onSubmit(value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onSubmit(value); }
        else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      }}
    />
  );
}

const sidebarCss = `
  .sidebar {
    width: 280px; flex-shrink: 0;
    background: white;
    border-right: 1px solid var(--border);
    display: flex; flex-direction: column;
    height: 100%;
    transition: width 200ms;
  }
  .sidebar.collapsed { width: 48px; align-items: center; padding-top: 14px; gap: 6px; }
  .rail-btn { width: 32px; height: 32px; border-radius: var(--r-md); display: grid; place-content: center; color: var(--ink-500); border: 0; background: transparent; cursor: pointer; }
  .rail-btn:hover { background: var(--ink-100); color: var(--ink-800); }

  .sb-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  .sb-title { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 13px; color: var(--ink-800); }
  .sb-title svg { color: var(--brand-500); }
  .sb-count {
    background: var(--brand-500); color: white; border-radius: var(--r-full);
    padding: 1px 7px; font-size: 11px; font-weight: 700;
  }
  .sb-actions { display: flex; align-items: center; gap: 4px; }

  .sb-scroll { overflow-y: auto; flex: 1; padding: 8px 0 24px; }

  .facet-group { padding: 6px 16px 10px; border-bottom: 1px solid var(--ink-100); }
  .facet-head {
    width: 100%; display: flex; align-items: center; justify-content: space-between;
    padding: 8px 0;
    font-weight: 700; font-size: 12.5px; color: var(--ink-700);
    text-transform: uppercase; letter-spacing: 0.04em;
    border: 0; background: transparent; cursor: pointer;
  }
  .facet-head .left { display: flex; align-items: center; gap: 8px; }
  .facet-head .right { display: flex; align-items: center; gap: 8px; }
  .facet-head svg { color: var(--ink-400); }
  .facet-head .chev { transition: transform 160ms; }
  .facet-head.closed .chev { transform: rotate(-90deg); }
  .facet-badge {
    background: var(--brand-500); color: white; border-radius: var(--r-full);
    padding: 1px 7px; font-size: 10px; font-weight: 800;
    letter-spacing: 0.02em; text-transform: none;
    font-variant-numeric: tabular-nums;
  }
  .facet-action { font-size: 11px; text-transform: none; letter-spacing: 0; }
  .facet-body { padding: 4px 0 8px; display: flex; flex-direction: column; gap: 2px; }

  .nested-group { margin: 0 -8px; }
  .nested-group .facet-group { padding: 0 8px; border-bottom: 0; }
  .nested-group .facet-head { padding: 6px 0; font-size: 11.5px; color: var(--ink-500); letter-spacing: 0.05em; }
  .nested-group .facet-head svg { color: var(--ink-300); }
  .nested-group .facet-body { padding-left: 4px; }
  .facet-sub { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--ink-400); padding: 4px 0; }

  .facet-row {
    display: flex; align-items: center; gap: 10px;
    padding: 5px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 100ms;
  }
  .facet-row:hover { background: var(--ink-100); }
  .facet-row .lbl { flex: 1; font-size: 13px; color: var(--ink-800); display: flex; align-items: center; gap: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .facet-row .num { font-size: 12px; color: var(--ink-400); font-variant-numeric: tabular-nums; }

  .view-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 1px; }
  .view-item {
    display: flex; align-items: center; gap: 10px;
    padding: 7px 10px;
    border-radius: 6px;
    font-size: 13px; color: var(--ink-700);
    cursor: pointer;
  }
  .view-item:hover { background: var(--ink-100); color: var(--ink-900); }
  .view-item:hover .view-act { opacity: 1; }
  .view-item.active { background: var(--brand-50); color: var(--brand-700); font-weight: 600; }
  .view-item.active svg { color: var(--brand-600); }
  .view-item.active .view-act { opacity: 1; }
  .view-item.dim { color: var(--ink-500); font-style: italic; }
  .view-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .view-act {
    width: 20px; height: 20px; border-radius: 50%;
    display: grid; place-content: center;
    color: var(--ink-400);
    border: 0; background: transparent; cursor: pointer;
    opacity: 0;
    transition: opacity 120ms;
  }
  .view-act:hover { color: var(--ink-800); background: var(--ink-100); }
  .view-act:last-child:hover { color: var(--rose-500); }
  .rename-input {
    flex: 1; min-width: 0;
    height: 22px; padding: 0 6px;
    font-size: 13px; color: var(--ink-900);
    border: 1px solid var(--brand-300);
    border-radius: 4px;
    background: white;
    outline: none;
  }
  .rename-input:focus { border-color: var(--brand-500); box-shadow: var(--shadow-focus); }

  .view-update {
    display: flex; align-items: center; gap: 8px;
    margin: 2px 4px 6px;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px; font-weight: 600; color: var(--brand-700);
    background: var(--brand-50);
    border: 1px dashed var(--brand-300);
    cursor: pointer;
  }
  .view-update:hover { background: var(--brand-100); border-style: solid; }
  .view-update svg { color: var(--brand-500); }
  .view-update span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .range-row { display: flex; gap: 6px; }
  .range-row .input { height: 32px; font-size: 13px; }
`;
